import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import EntriesTable from '../../../components/entriesTable';
import Pill from '../../../components/pill';
import SearchField from '../../../components/searchField';
import SortDropdown from '../../../components/sortDropdown';
import { ROLES } from '../../../config/permissions/roles';
import { isAwardType } from '../../../helpers/awardTypes';
import { useJuror } from '../../../hooks/useAuth';
import useSubmissionsFilter from '../../../hooks/useSubmissionsFilter';
import { AWARD_TYPES } from '../../../models/award';
import { EVALUATION_STATUS } from '../../../models/submission';
import SubmissionOverview from '../../../models/submissionOverview';
import Hero from '../../../modules/hero';
import styles from './styles.module.scss';

const filterDecidedEvaluations = (evaluation) =>
  evaluation.status === EVALUATION_STATUS.decided ||
  evaluation.status === EVALUATION_STATUS.decidedByChairperson;

const Entries = () => {
  const [submissions, setSubmissions] = useState([]);
  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);
  const [visibleSubmissions, setVisibleSubmissions] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [searchParams, _] = useSearchParams();
  const { juror, jurorGroup, role, session } = useJuror();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    filterParams,
    filterOptions,
    handleSortChange,
    handleFilterReset,
    handleFilterDeselect,
    onFulltextSearch,
    onSort,
  } = useSubmissionsFilter({ submissions, setVisibleSubmissions });
  const [searchTerm, setSearchTerm] = useState(filterParams?.search);

  useEffect(() => {
    if (
      visibleSubmissions.length &&
      state?.submissionId &&
      !hasScrolledIntoView
    ) {
      // use timeout to allow rendering
      setTimeout(() => {
        const node = document.querySelector(
          `#submission_${state.submissionId}`
        );
        if (node) {
          node.scrollIntoView({ behavior: 'smooth' });
          setHasScrolledIntoView(true);
        }
      }, 100);
    }
  }, [visibleSubmissions, setHasScrolledIntoView]);

  useEffect(async () => {
    if (!juror?.jurorId || !jurorGroup?.jurorGroupId || !session) return;
    let data = [];

    if (
      isAwardType(AWARD_TYPES.IF_SIP, session) &&
      session?.prizeMoneyModeEnabled
    ) {
      data = await SubmissionOverview.fetchAllSubmissionsForSession({
        sessionId: session.sessionId,
      });
    } else {
      if (role === ROLES.chairPerson) {
        data = (
          await Promise.all(
            juror?.jurorGroups.map((group) =>
              SubmissionOverview.fetchAllSubmissionsForJurorGroup({
                jurorGroupId: group.jurorGroupId,
                extraFields: ['presentationForm'],
              })
            )
          )
        ).flat();
      } else {
        data = await SubmissionOverview.fetchAllSubmissionsForJurorGroup({
          jurorGroupId: jurorGroup?.jurorGroupId,
          extraFields: ['presentationForm'],
        });
      }
    }
    data = data.map((submission) => {
      const decided = submission.evaluations.filter(filterDecidedEvaluations);

      let averageScoreFromPriorSession =
        (submission?.scoresFromPriorSessions &&
          Math.floor(
            submission?.scoresFromPriorSessions?.[0]?.totalEvaluationPoints /
              submission?.scoresFromPriorSessions?.[0]?.numDecidedEvaluations
          )) ||
        null;
      return {
        ...submission,
        averageScore: decided.length
          ? decided.reduce((prev, cur) => prev + cur.totalPoints, 0) /
            decided.length
          : 0,
        averageScoreFromPriorSession,
      };
    });

    setSubmissions(data);
    setEntriesLoading(false);
  }, [juror, jurorGroup, session]);

  const handleSubmissionUpdate = (updatedSubmission, updatedKey) => {
    setSubmissions((prev) =>
      prev.map((submission) =>
        submission.submissionId === updatedSubmission.submissionId
          ? updatedSubmission
          : submission
      )
    );
  };

  const handleSortChangeClick = (value) => {
    handleSortChange(value);
  };

  const navigateToDetail = (path, submission, extraState) => {
    const jurorGroupId = submission?.evaluations[0]?.jurorGroupId;
    const params = searchParams ? '?' + searchParams : '';
    let state = {
      submissionsList: visibleSubmissions,
    };
    if (
      isAwardType(AWARD_TYPES.IF_SIP, session) &&
      session.prizeMoneyModeEnabled
    ) {
      state = {
        ...state,
        ...extraState,
      };
    }

    if (role === ROLES.chairPerson) {
      state = {
        ...state,
        jurorGroupId,
      };
    }

    navigate(location.pathname, {
      replace: true,
      state: {
        submissionId: path.split('/').pop(),
      },
    });
    navigate(path.replace(/\s/g, '') + params, {
      state,
    });
  };

  const debounceFullTextSearch = useCallback(
    debounce(onFulltextSearch, 300),
    []
  );

  const onSearch = (val) => {
    setSearchTerm(val);
    debounceFullTextSearch(val);
  };

  return (
    <div className={styles.entries}>
      <Hero
        title={
          <Trans
            i18nKey="jurorEntries.title"
            components={{ bold: <b /> }}
            values={{
              numEntries: visibleSubmissions.length,
            }}
          />
        }
      />
      <Container>
        <div className={styles.toolbar}>
          <div className={styles.filterPills}>
            {filterParams.data.map((filterParam) => (
              <Pill
                key={filterParam}
                handleClick={() => handleFilterDeselect(filterParam)}
                text={
                  filterParam.split('__')[1] ||
                  filterOptions.find((item) => item.key === filterParam)?.text
                }
              />
            ))}
            {filterParams.order && (
              <Pill
                handleClick={() => handleFilterReset('order')}
                text={t(`jurorEntries.filter.order.${filterParams.order}`)}
              />
            )}
          </div>
          <SearchField value={searchTerm} onChange={onSearch} />
          <SortDropdown
            activeOptions={filterParams.data}
            options={filterOptions}
            onChange={handleSortChangeClick}
          />
        </div>
        <div className={styles.tableContainer}>
          <EntriesTable
            submissions={visibleSubmissions}
            updateSubmission={handleSubmissionUpdate}
            loading={entriesLoading}
            onEntryClick={navigateToDetail}
            onSort={onSort}
          />
        </div>
      </Container>
    </div>
  );
};

export default Entries;
